import { useState, useEffect } from "react";
import api from "../constant/api"; // Ungal API instance path
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, PlusCircle, Edit3, Trash2, FileText, Folder } from "lucide-react";

export default function AdminAuthManager() {
  const [activeTab, setActiveTab] = useState("login"); // login, register, forgot, list
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  // 📝 Master menus storage state
  const [menuGroups, setMenuGroups] = useState([]); 

  // Modal Control States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // 🎯 Matrix Matrix: இதில் ["1_view", "1_create", "2_edit"] போன்ற மதிப்புகள் சேமிக்கப்படும்
  const [selectedPermissions, setSelectedPermissions] = useState([]); 

  // Forms States
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", phone: "", password: "", role: "user" });
  const [forgotForm, setForgotForm] = useState({ email: "", newPassword: "" });

  // 🔄 1. FETCH DYNAMIC MENUS FROM THE DB API
  const loadMenus = async () => {
    try {
      const res = await api.get("/menus/getMenus");
      if (res.data?.success) {
        setMenuGroups(res.data.data);
      }
    } catch (err) {
      toast.error("Failed to fetch layouts data tracking");
    }
  };

  // Function to load users list
  const fetchUsers = () => {
    api.get("/api/admin/users")
      .then(res => res.data.success ? setUsers(res.data.data) : toast.error(res.data.msg))
      .catch(() => toast.error("Error fetching admin list"));
  };

  // 🔄 2. CORE EFFECT: Loads API data on startup or tab change
  useEffect(() => {
    loadMenus(); 
    if (activeTab === "list") {
      fetchUsers();
    }
  }, [activeTab]);

  // 🚀 OPEN CONTROL MODAL - FETCHING MATRIX DATA LIVE FROM THE DATABASE
  const openControlModal = async (user) => {
    setSelectedUser(user);
    setSelectedPermissions([]); // ஆரம்பத்தில் பழைய டேட்டாவை ரீசெட் செய்கிறது

    try {
      // 💡 பின்தளத்தில் நாம் உருவாக்கிய புதிய மல்டிபிள் செக்பாக்ஸ் ஏபிஐ குவரியை அழைக்கிறோம்
      const res = await api.get(`/api/admin/get-allowed-menus?email=${user.email}`);
      if (res.data?.success) {
        // டேட்டாபேஸில் இருந்து வரும் ["1_view", "1_create", "2_edit"] எரே அப்படியே செட் ஆகும்
        setSelectedPermissions(res.data.data || []);
      } else {
        setSelectedPermissions([]);
      }
    } catch (err) {
      toast.error("Error syncing active permissions from database ❌");
      setSelectedPermissions([]);
    }
    
    setIsModalOpen(true);
  };

  // 🔄 MULTIPLE ACTION MATRIX TOGGLE FUNCTION
  const handlePermissionToggle = (menuId, action) => {
    const permissionKey = `${menuId}_${action}`; // Output Sample: "12_view", "12_edit"
    
    if (selectedPermissions.includes(permissionKey)) {
      // ஏற்கனவே டிக் செய்திருந்தால் நீக்குகிறது
      setSelectedPermissions(selectedPermissions.filter(p => p !== permissionKey));
    } else {
      // டிக் செய்யவில்லை என்றால் புதிதாக சேர்க்கிறது
      setSelectedPermissions([...selectedPermissions, permissionKey]);
    }
  };

  // 🎛️ ONE-CLICK MASTER ROW SELECT/DESELECT
  const toggleWholeRow = (menuId) => {
    const actions = ["view", "create", "edit", "delete", "pdf"];
    const rowKeys = actions.map(act => `${menuId}_${act}`);
    const hasAll = rowKeys.every(k => selectedPermissions.includes(k));

    if (hasAll) {
      // முழு ரோவையும் அன்-செக் செய்கிறது
      setSelectedPermissions(selectedPermissions.filter(p => !rowKeys.includes(p)));
    } else {
      // முழு ரோவிற்கும் அனுமதி வழங்குகிறது
      const filteredExisting = selectedPermissions.filter(p => !rowKeys.includes(p));
      setSelectedPermissions([...filteredExisting, ...rowKeys]);
    }
  };

  // 💾 SAVE MULTIPLE PERMISSION MATRIX TO BACKEND (INSERT INTO ... ON DUPLICATE KEY UPDATE)
  const handleUpdatePermissions = () => {
    api.put(`/api/admin/update-permissions`, { 
      email: selectedUser.email,
      permitted_menus: selectedPermissions 
    })
    .then(res => {
      if (res.data.success) {
        toast.success("Granular actions updated to Matrix Table! 🚀");
        setIsModalOpen(false);
        fetchUsers(); 
      } else {
        toast.error(res.data.msg);
      }
    })
    .catch(() => toast.error("Server connection error ❌"));
  };

  // Handle Actions
  const handleLogin = (e) => {
    e.preventDefault();
    api.post("/api/admin/login", loginForm).then(res => {
      if (res.data.success) {
        localStorage.setItem("token", "secure-admin-token");
        localStorage.setItem("userEmail", res.data.user.email);
        localStorage.setItem("userRole", res.data.user.role);
        window.dispatchEvent(new Event("settingsSaved"));
        toast.success("Welcome Back! 🎉");
        navigate("/");
      } else { toast.error(res.data.msg); }
    });
  };

  const handleRegister = (e) => {
    e.preventDefault();
    api.post("/api/admin/register", registerForm).then(res => {
      if (res.data.success) {
        toast.success(res.data.msg);
        setActiveTab("list");
      } else { toast.error(res.data.msg); }
    });
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    api.post("/api/admin/forgot-password", forgotForm).then(res => {
      if (res.data.success) {
        toast.success(res.data.msg);
        setActiveTab("login");
      } else { toast.error(res.data.msg); }
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6 flex flex-col items-center justify-center">
      {/* Tab Switches */}
      <div className="flex space-x-2 bg-white p-2 rounded-xl shadow-sm mb-6 border">
        <button onClick={() => setActiveTab("register")} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'register' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>CREATE ADMIN</button>
        <button onClick={() => setActiveTab("forgot")} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'forgot' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>FORGOT PASS</button>
        <button onClick={() => setActiveTab("list")} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>ADMINS LIST</button>
      </div>

      <div className="w-full max-w-md bg-white p-6 md:p-8 rounded-2xl shadow-md border border-slate-100">
        {/* LOGIN VIEW */}
        {activeTab === "login" && (
          <form onSubmit={handleLogin} className="space-y-4">
            <h3 className="text-xl font-black text-slate-800 text-center uppercase">Admin Login</h3>
            <input type="email" placeholder="Email" required onChange={e => setLoginForm({...loginForm, email: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <input type="password" placeholder="Password" required onChange={e => setLoginForm({...loginForm, password: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <button className="w-full bg-blue-600 text-white py-2.5 rounded-xl font-bold text-sm shadow hover:bg-blue-700">Access Portal</button>
          </form>
        )}

        {/* REGISTER VIEW */}
        {activeTab === "register" && (
          <form onSubmit={handleRegister} className="space-y-3">
            <h3 className="text-xl font-black text-slate-800 text-center uppercase">Register Admin</h3>
            <input type="text" placeholder="Full Name" required onChange={e => setRegisterForm({...registerForm, name: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <input type="email" placeholder="Email Address" required onChange={e => setRegisterForm({...registerForm, email: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <input type="text" placeholder="Phone String" onChange={e => setRegisterForm({...registerForm, phone: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <input type="password" placeholder="Secret Password" required onChange={e => setRegisterForm({...registerForm, password: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <select value={registerForm.role} onChange={e => setRegisterForm({...registerForm, role: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl bg-slate-50 font-semibold text-slate-700">
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
            <button className="w-full bg-green-600 text-white py-2.5 rounded-xl font-bold text-sm shadow hover:bg-green-700">Save Account</button>
          </form>
        )}

        {/* FORGOT PASSWORD VIEW */}
        {activeTab === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <h3 className="text-xl font-black text-slate-800 text-center uppercase">Reset Password</h3>
            <input type="email" placeholder="Registered Email Address" required onChange={e => setForgotForm({...forgotForm, email: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <input type="password" placeholder="New Secret Password" required onChange={e => setForgotForm({...forgotForm, newPassword: e.target.value})} className="w-full border p-2.5 text-sm rounded-xl focus:outline-none" />
            <button className="w-full bg-red-600 text-white py-2.5 rounded-xl font-bold text-sm shadow hover:bg-red-700">Override Password</button>
          </form>
        )}

        {/* ADMIN USER LIST VIEW */}
        {activeTab === "list" && (
          <div className="space-y-4">
            <h3 className="text-xl font-black text-slate-800 text-center uppercase">System Admins</h3>
            <p className="text-[10px] text-slate-400 font-bold tracking-wider text-center bg-slate-50 py-1 rounded-md">👇 Click user to configure Custom Checkbox Controls</p>
            <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
              {users.map(u => (
                <div 
                  key={u.admin_id} 
                  onClick={() => openControlModal(u)} 
                  className="p-3 bg-slate-50 hover:bg-indigo-50/40 cursor-pointer rounded-xl border border-slate-200 transition-all flex justify-between items-center text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-800">{u.name}</p>
                    <p className="text-slate-500 text-[11px] truncate">{u.email}</p>
                  </div>
                  <span className={`ml-2 shrink-0 px-2 py-0.5 rounded-md font-black uppercase text-[9px] ${u.role === 'superadmin' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{u.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 🚀 RESPONSIVE GRANULAR ACTION ROW MATRIX MODAL WINDOW */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xs flex items-center justify-center p-2 md:p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-4 md:p-6 w-full max-w-2xl shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
            
            {/* Header Content */}
            <div className="text-center mb-3 shrink-0">
              <h4 className="text-base font-black text-slate-900 uppercase tracking-wide">Action Permission Matrix</h4>
              <p className="text-xs text-indigo-600 font-black mt-0.5">Configuring: {selectedUser.name} ({selectedUser.role})</p>
            </div>

            <hr className="border-slate-100 mb-3 shrink-0" />

            {/* Matrix Sheet Columns Labels Header */}
            <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-1 bg-slate-100 rounded-lg text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2 shrink-0">
              <div className="col-span-4 flex items-center">Modules Layout</div>
              <div className="col-span-8 grid grid-cols-5 text-center">
                <span className="flex items-center justify-center gap-1 text-slate-700"><Eye size={11}/> View</span>
                <span className="flex items-center justify-center gap-1 text-emerald-600"><PlusCircle size={11}/> New</span>
                <span className="flex items-center justify-center gap-1 text-amber-600"><Edit3 size={11}/> Edit</span>
                <span className="flex items-center justify-center gap-1 text-rose-600"><Trash2 size={11}/> Del</span>
                <span className="flex items-center justify-center gap-1 text-indigo-600"><FileText size={11}/> PDF</span>
              </div>
            </div>

            {/* Matrix Body Loops */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-1 text-xs">
              {menuGroups.length === 0 ? (
                <p className="text-xs text-slate-400 font-bold text-center py-8">No active database menus tracked ❌</p>
              ) : (
                menuGroups.map((group, groupIdx) => (
                  <div key={group.groupName || groupIdx} className="space-y-1.5 bg-slate-50/50 p-2 rounded-xl border border-slate-100">
                    
                    {/* Folder Group Title Banner */}
                    <div className="text-[10px] font-black text-indigo-600 uppercase tracking-wider bg-indigo-50/60 px-2.5 py-1 rounded-lg flex items-center gap-1.5">
                      <Folder size={12} /> {group.groupName}
                    </div>

                    {/* Sub-menus Matrix Rows mapping loop */}
                    {group.items && group.items.map((menu) => {
                      const mId = String(menu.id);
                      const mTitle = menu.title;
                      
                      // Check if total row has full permissions granted
                      const rowKeys = ["view", "create", "edit", "delete", "pdf"].map(act => `${mId}_${act}`);
                      const isWholeRowChecked = rowKeys.every(k => selectedPermissions.includes(k));

                      return (
                        <div key={mId} className="bg-white border border-slate-200/60 p-2.5 rounded-xl sm:grid sm:grid-cols-12 gap-2 items-center hover:border-slate-300 transition-all">
                          
                          {/* Left Column: Title + Master Checkbox */}
                          <div className="col-span-4 flex items-center justify-between sm:justify-start gap-2 mb-2 sm:mb-0 min-w-0">
                            <span className="font-bold text-slate-700 truncate">
                              📄 {mTitle} <span className="text-[9px] text-slate-400 font-normal">({mId})</span>
                            </span>
                            <button 
                              type="button" 
                              onClick={() => toggleWholeRow(mId)} 
                              className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase transition-all tracking-tight ${isWholeRowChecked ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                            >
                              {isWholeRowChecked ? 'All ON' : 'Toggle'}
                            </button>
                          </div>

                          {/* Right Column: Five Column Interactive Action Matrix Cells */}
                          <div className="col-span-8 grid grid-cols-5 gap-1 text-center">
                            {["view", "create", "edit", "delete", "pdf"].map((action) => {
                              const isChecked = selectedPermissions.includes(`${mId}_${action}`);
                              
                              const activeColors = {
                                view: "bg-blue-50 border-blue-200 text-blue-700",
                                create: "bg-emerald-50 border-emerald-200 text-emerald-700",
                                edit: "bg-amber-50 border-amber-200 text-amber-700",
                                delete: "bg-rose-50 border-rose-200 text-rose-700",
                                pdf: "bg-indigo-50 border-indigo-200 text-indigo-700"
                              };

                              return (
                                <div
                                  key={action}
                                  onClick={() => handlePermissionToggle(mId, action)}
                                  className={`flex flex-col sm:flex-row items-center justify-center p-1 sm:p-1.5 border rounded-lg cursor-pointer select-none transition-all gap-1 ${isChecked ? activeColors[action] : 'bg-slate-50/30 border-slate-200 text-slate-400 hover:bg-slate-100/70'}`}
                                >
                                  {/* Mobile Action Text Label */}
                                  <span className="text-[8px] font-black uppercase sm:hidden tracking-tighter">
                                    {action === 'create' ? 'new' : action === 'delete' ? 'del' : action}
                                  </span>
                                  
                                  <input 
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {}} 
                                    className="h-3 w-3 rounded text-slate-600 focus:ring-0 border-slate-300 cursor-pointer pointer-events-none"
                                  />
                                </div>
                              );
                            })}
                          </div>

                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Modal Bottom Action Controls Footer */}
            <div className="flex space-x-2 mt-4 shrink-0">
              <button 
                onClick={() => setIsModalOpen(false)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-xl font-bold text-xs transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleUpdatePermissions}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl font-bold text-xs shadow-md transition-colors"
              >
                Save Matrix Permissions
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}